import { Box, Typography } from '@mui/material'
import { useHasBuiltinRole, useRoleChecker, useUnsafeFeatureChecker } from 'components/no-ui/capability'
import { RoleID } from 'lib/constants/system'
import { FC } from 'react'

import { settingsNavigation } from './config'
import { useSystemManagementWarningContext } from './ModularC1Nav'
import { NavItem } from './NavItem'
import { SidebarOverlay } from './SidebarOverlay'
import { CustomFlyoutProps, NavModule, SettingsGroup } from './types'

export const SettingsSidebarOverlay: FC<CustomFlyoutProps> = ({ onRef, label, isPersistent }) => {
    const hasRole = useRoleChecker()
    const hasFeature = useUnsafeFeatureChecker()
    const isReadOnlySuperAdmin = useHasBuiltinRole(RoleID.ReadOnlySuperAdmin)
    const systemManagementWarning = useSystemManagementWarningContext()

    const filterItems = (items: NavModule[]): NavModule[] => {
        return items.filter((item) => {
            if (isReadOnlySuperAdmin) return true
            const meetsRole = item.roles?.length ? item.roles.some(hasRole) : true
            const meetsFeature = item.features?.length ? item.features.some(hasFeature) : true
            const noHideFeature = item.hideOnFeatures?.length
                ? !item.hideOnFeatures.some(hasFeature)
                : true
            return meetsRole && meetsFeature && noHideFeature && !item.placeholder
        })
    }

    return (
        <SidebarOverlay onRef={onRef} label={label} isPersistent={isPersistent}>
            <Box sx={{ py: 2, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                {settingsNavigation.map((group) => {
                    const visibleItems = filterItems(group.items)
                    if (visibleItems.length === 0) return null

                    return (
                        <SettingsGroupSection
                            key={group.id}
                            group={group}
                            items={visibleItems}
                            systemManagementWarning={systemManagementWarning}
                        />
                    )
                })}
            </Box>
        </SidebarOverlay>
    )
}

type SettingsGroupSectionProps = {
    group: SettingsGroup
    items: NavModule[]
    systemManagementWarning: { showWarning: boolean; tooltipContent: React.ReactNode | null }
}

const SettingsGroupSection: FC<SettingsGroupSectionProps> = ({ group, items, systemManagementWarning }) => {
    return <Box sx={{ mb: 4 }}>
        <Typography
            variant="caption"
            sx={{
                px: 4,
                pb: 0,
                display: 'block',
                color: 'text.secondary',
            }}
        >
            {group.label}
        </Typography>

        <Box>
            {items.map((item) => {
                const isSystemManagement = item.id === 'system-management'
                return (
                    <NavItem
                        key={item.id}
                        item={item}
                        label={item.label}
                        showWarning={isSystemManagement && systemManagementWarning.showWarning}
                        warningTooltipContent={systemManagementWarning.tooltipContent}
                    />
                )
            })}
        </Box>
    </Box>
}
